/**
 * Type definitions for Help Chat Widgets
 * 
 * These JSDoc type definitions provide structure and documentation for the
 * Help Chat widget system. Use them for type checking and IDE autocomplete.
 * 
 * @see README.md for full specification
 */

/**
 * @typedef {"user" | "bot"} MessageRole
 * Role of the message sender
 */

/**
 * @typedef {Object} ChatMessage
 * Complete chat message structure (parent message shape)
 * @property {MessageRole} Role - Sender role: "user" or "bot"
 * @property {string} Message - The message text content
 * @property {ChatWidget} [Widget] - Optional widget (bot messages only)
 * @property {string} Time - ISO 8601 timestamp
 */

/**
 * @typedef {Object} ChatWidget
 * Widget payload attached to bot messages
 * @property {string} widgetID - Registered widget type identifier
 * @property {WidgetData} widgetData - Widget-specific data payload
 */

/**
 * @typedef {LinkWidgetData | Object} WidgetData
 * Widget-specific data (discriminated by widgetID)
 */

/**
 * @typedef {Object} LinkWidgetData
 * Data payload for the "link" widget (v1)
 * @property {string} label - Display text on the pill button
 * @property {string | Object} route - Vue Router path string (preferred) or named location object
 * 
 * @example
 * // String path (preferred)
 * { label: "View Design Changes", route: "/Database/DesignChanges/DC-2024-001" }
 * 
 * @example
 * // Named location object (only if necessary)
 * { label: "View Design", route: { name: "DesignChanges", params: { id: "DC-2024-001" } } }
 */

/**
 * @typedef {Object} WidgetRendererProps
 * Props passed to widget renderer components
 * @property {WidgetData} widgetData - The widget-specific data
 * @property {string} [messageId] - Optional: ID of the parent message
 * @property {string} [conversationId] - Optional: ID of the conversation
 */

// Example chat message instances for reference

/**
 * Example: Bot message with link widget
 * @type {ChatMessage}
 */
export const exampleBotMessageWithLink = {
  Role: "bot",
  Message: "I found the design changes you were looking for.",
  Widget: {
    widgetID: "link",
    widgetData: {
      label: "View Design Changes",
      route: "/Database/DesignChanges/DC-2024-001"
    }
  },
  Time: "2025-01-15T10:30:00.000Z"
};

/**
 * Example: Bot message without widget
 * @type {ChatMessage}
 */
export const exampleBotMessageNoWidget = {
  Role: "bot",
  Message: "Hello! How can I help you today?",
  Time: "2025-01-15T10:29:00.000Z"
};

/**
 * Example: User message (never has widgets)
 * @type {ChatMessage}
 */
export const exampleUserMessage = {
  Role: "user",
  Message: "Can you show me the latest design changes?",
  Time: "2025-01-15T10:28:00.000Z"
};

/**
 * Example: Bot message with unknown widgetID (should ignore widget, show message)
 * @type {ChatMessage}
 */
export const exampleBotMessageUnknownWidget = {
  Role: "bot",
  Message: "This is a message with an unknown widget type.",
  Widget: {
    widgetID: "unknown-future-widget",
    widgetData: {
      someField: "someValue"
    }
  },
  Time: "2025-01-15T10:31:00.000Z"
};

/**
 * Validate if a message has a valid widget structure
 * 
 * @param {ChatMessage} message - The chat message to validate
 * @returns {boolean} - True if message has a structurally valid widget
 */
export function hasValidWidget(message) {
  return (
    message.Role === "bot" &&
    message.Widget &&
    typeof message.Widget.widgetID === "string" &&
    typeof message.Widget.widgetData === "object" &&
    message.Widget.widgetData !== null
  );
}

/**
 * Extract widget data with type checking
 * 
 * @param {ChatMessage} message - The chat message
 * @param {string} expectedWidgetID - The expected widget type
 * @returns {WidgetData|null} - Widget data if valid and matches expected type, null otherwise
 */
export function getWidgetData(message, expectedWidgetID) {
  if (!hasValidWidget(message)) {
    return null;
  }
  
  if (message.Widget.widgetID !== expectedWidgetID) {
    return null;
  }
  
  return message.Widget.widgetData;
}

/**
 * Check if a route is a valid internal route (not external URL)
 * 
 * Rejects:
 * - Empty strings
 * - Protocol-prefixed strings (http:, https:, javascript:, data:, mailto:, ftp:, etc.)
 * - Protocol-relative URLs (//)
 * - String paths not starting with /
 * 
 * Accepts:
 * - String paths starting with / (e.g., "/Database/JobCards/123")
 * - Named location objects with non-empty string name
 * 
 * @param {string | Object} route - The route to validate
 * @returns {boolean} - True if route is valid for in-app navigation
 */
export function isValidInternalRoute(route) {
  if (typeof route === "object") {
    // Named location object: require non-empty string name
    if (route === null) {
      return false;
    }
    return typeof route.name === "string" && route.name.length > 0;
  }
  
  if (typeof route === "string") {
    // String path: must start with / and not contain protocol
    if (route.length === 0) {
      return false;
    }
    
    // Reject protocol-relative and any protocol-prefixed strings
    if (route.startsWith("//") || route.includes(":")) {
      return false;
    }
    
    // Require leading /
    if (!route.startsWith("/")) {
      return false;
    }
    
    return true;
  }
  
  return false;
}
